import * as React from "react";
import * as Comlink from "comlink";
import { createWorker, wrap } from "#impl/worker";

import {
  WorkersContext,
  ProxiedWorker,
  WorkerMap,
  WorkerId,
  WorkerRegistry,
} from "./context";
import { UrlMapping, WorkerKey, ApiMapping } from "./mapping";
import { randomId } from "@/utils/random";

const DEFAULT_RELEASE_DELAY = 1000;

const claimWorker = <W extends WorkerKey>(
  workers: WorkerMap<W>,
  id: WorkerId,
) => {
  const worker = workers[id];
  if (!worker) {
    throw new Error(`Worker with id ${id} not found.`);
  }
  if (worker.release) {
    clearTimeout(worker.release);
    worker.release = undefined;
  } else {
    throw new Error(`Worker with id ${id} locked.`);
  }
};

const deleteWorker = <W extends WorkerKey>(
  workers: WorkerMap<W>,
  id: WorkerId,
) => {
  const worker = workers[id];
  if (worker) {
    delete workers[id];
    worker.proxy[Comlink.releaseProxy]();
    worker.inner.terminate();
    worker.terminated = true;
  } else {
    console.warn(`Worker with id ${id} already deleted or not found`);
  }
};

const releaseWorker = <W extends WorkerKey>(
  workers: WorkerMap<W>,
  id: WorkerId,
  releaseDelay: number,
) => {
  if (!(id in workers)) {
    console.warn(`Worker with id ${id} not found in registry.`);
    return;
  }
  const worker = workers[id];
  if (worker.release) {
    console.warn(`Worker with id ${id} already released.`);
  } else {
    worker.release = setTimeout(() => {
      deleteWorker(workers, id);
    }, releaseDelay);
  }
};

const refreshWorker = <W extends WorkerKey>(
  workers: WorkerMap<W>,
  id: WorkerId,
  releaseDelay: number,
) => {
  claimWorker(workers, id);
  releaseWorker(workers, id, releaseDelay);
};

const createProxiedWorker = <W extends WorkerKey>(
  registry: WorkerRegistry,
  key: W,
  releaseDelay: number,
): ProxiedWorker<W> => {
  const workers = registry[key] as WorkerMap<W>;
  for (const worker of Object.values(workers)) {
    if (worker.release) {
      worker.releaseDelay = releaseDelay;
      refreshWorker(workers, worker.id, releaseDelay);
      return worker;
    }
  }
  const id = randomId();
  const inner = createWorker(UrlMapping[key]);
  const proxy = wrap<ApiMapping[W]>(inner);
  const worker = {
    id,
    key,
    inner,
    proxy,
    releaseDelay,
    terminated: false,
  };
  workers[id] = worker;
  releaseWorker(workers, id, releaseDelay);
  return worker;
};

interface WorkerLock<W extends WorkerKey> {
  worker: ProxiedWorker<W>;
  claim: () => () => void;
  restart: () => void;
}

const useWorkerLock = <W extends WorkerKey>(
  key: W,
  releaseDelay: number = DEFAULT_RELEASE_DELAY,
): WorkerLock<W> => {
  const isMounted = React.useRef(false);
  const { registry } = React.useContext(WorkersContext);
  const [worker, setWorker] = React.useState<ProxiedWorker<W>>(() => {
    return createProxiedWorker(registry, key, releaseDelay);
  });
  const claim = React.useCallback(() => {
    const { key, id, releaseDelay } = worker;
    const workers = registry[key] as WorkerMap<W>;
    claimWorker(workers, id);
    return () => {
      if (!worker.terminated) {
        releaseWorker(workers, id, releaseDelay);
      }
    };
  }, [registry, worker]);
  const restart = React.useCallback(() => {
    const { key, id, releaseDelay } = worker;
    const workers = registry[key] as WorkerMap<W>;
    deleteWorker(workers, id);
    setWorker(createProxiedWorker(registry, key, releaseDelay));
  }, [registry, worker]);

  React.useEffect(() => {
    isMounted.current = false;
  }, []);

  React.useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setWorker(createProxiedWorker(registry, key, releaseDelay));
  }, [registry, key, releaseDelay]);

  return {
    worker,
    claim,
    restart,
  };
};

export const useEnsureWorker = <W extends WorkerKey>(
  key: W,
  releaseDelay: number = DEFAULT_RELEASE_DELAY,
) => {
  useWorkerLock(key, releaseDelay);
};

interface UseComlink<W extends WorkerKey> {
  id: WorkerId;
  worker: Comlink.Remote<ApiMapping[W]>;
  restart: () => void;
}
export const useComlink = <W extends WorkerKey>(
  key: W,
  releaseDelay: number = DEFAULT_RELEASE_DELAY,
): UseComlink<W> => {
  const {
    worker: { id, proxy },
    claim,
    restart,
  } = useWorkerLock<W>(key, releaseDelay);
  React.useEffect(() => {
    return claim();
  }, [claim]);
  return { id, worker: proxy, restart };
};
